<template>
  <div>
    <div v-if="displayType === 'long'">
      <p>
        Month-end trailing returns presented in this analysis are hypothetical in nature.
        They do not reflect actual investment results and are not guarantees of future results.

        Month-end returns are presented as of {{ lastDayOfMonth }}, adjusted for splits and dividends,
        assuming monthly rebalancing to current allocation and reinvestment of dividends and capital gains.
        Returns do not account for taxes, loads, sales charges, trading commissions, short-term trading fees, management fees, or advisory fees.
      </p>

      <p>
        Returns coverage includes Equities, Mutual Funds, ETFs, and Cash.
        <span v-if="portfolio">
          These returns calculations cover
          {{ portfolio.trailing_returns_monthly_rebalance?.[2].toLocaleString('en-US', {style: 'percent'}) }}
          of {{ portfolioName }} overall.
        </span>
        <span v-if="benchmark">
          These returns calculations cover
          {{ benchmark.trailing_returns_monthly_rebalance?.[2].toLocaleString('en-US', {style: 'percent'}) }}
          of {{ benchmarkName }} overall.
        </span>
        <span v-if="optimizedPortfolio">
          These returns calculations cover
          {{ optimizedPortfolio.trailing_returns_monthly_rebalance?.[2].toLocaleString('en-US', {style: 'percent'}) }}
          of {{ optimizedPortfolioName }} overall.
        </span>
      </p>

      <p>Below is the percent of the portfolio that has the full period of returns for each returns period:</p>
      <div v-if="portfolio">
        {{ portfolioName }}:
        1 year: {{ portfolio.trailing_returns_monthly_rebalance?.[1]?.trailing_1_yr_returns?.toLocaleString('en-US', {style: 'percent'}) }}
        3 year: {{ portfolio.trailing_returns_monthly_rebalance?.[1]?.trailing_3_yr_returns?.toLocaleString('en-US', {style: 'percent'}) }}
        5 year: {{ portfolio.trailing_returns_monthly_rebalance?.[1]?.trailing_5_yr_returns?.toLocaleString('en-US', {style: 'percent'}) }}
        10 year: {{ portfolio.trailing_returns_monthly_rebalance?.[1]?.trailing_10_yr_returns?.toLocaleString('en-US', {style: 'percent'}) }}
      </div>
      <div v-if="benchmark">
        {{ benchmarkName }}:
        1 year: {{ benchmark.trailing_returns_monthly_rebalance?.[1]?.trailing_1_yr_returns?.toLocaleString('en-US', {style: 'percent'}) }}
        3 year: {{ benchmark.trailing_returns_monthly_rebalance?.[1]?.trailing_3_yr_returns?.toLocaleString('en-US', {style: 'percent'}) }}
        5 year: {{ benchmark.trailing_returns_monthly_rebalance?.[1]?.trailing_5_yr_returns?.toLocaleString('en-US', {style: 'percent'}) }}
        10 year: {{ benchmark.trailing_returns_monthly_rebalance?.[1]?.trailing_10_yr_returns?.toLocaleString('en-US', {style: 'percent'}) }}
      </div>
      <div v-if="optimizedPortfolio">
        {{ optimizedPortfolioName }}:
        1 year: {{ optimizedPortfolio.trailing_returns_monthly_rebalance?.[1]?.trailing_1_yr_returns?.toLocaleString('en-US', {style: 'percent'}) }}
        3 year: {{ optimizedPortfolio.trailing_returns_monthly_rebalance?.[1]?.trailing_3_yr_returns?.toLocaleString('en-US', {style: 'percent'}) }}
        5 year: {{ optimizedPortfolio.trailing_returns_monthly_rebalance?.[1]?.trailing_5_yr_returns?.toLocaleString('en-US', {style: 'percent'}) }}
        10 year: {{ optimizedPortfolio.trailing_returns_monthly_rebalance?.[1]?.trailing_10_yr_returns?.toLocaleString('en-US', {style: 'percent'}) }}
      </div>
      <p>Monthly returns are calculated by multiplying the returns of each covered security in the portfolio by that security's current allocation in the portfolio using the following formula:</p>
      <p>
        Rp,n = Sum(Wi,n x Ri,n) from i=1 to m, where Rp,n is the portfolio returns at month n, Wi,n is the weight of asset i in month n, Ri,n is the monthly return of asset i in month n, and there are m securities with returns in month n.
      </p>
      <p>This process replicates month-end rebalancing. We assume Cash has 0% returns for all monthly periods.</p>

      When monthly returns are not available for a holding
      (e.g. the security did not exist during historical period being reported),
      the remaining portfolio holdings are re-weighted to maintain consistent ratios using the following formula:
      Wnew,i,n = Wcurrent,i x (SUMcovered(Wcurrent,i)) / (1-(SUMnot_covered(Wcurrent,i))),
      where Wnew,i,n is the adjusted weight of security i at month n,
      SUMnot_covered is the sum over all securities that do not have monthly returns available at month n,
      SUMcovered is the sum over all the securities that do have monthly returns available at month n, and
      Wcurrent is the current weight of security i.

      Returns over a period of time are calculated by applying the following formula to monthly returns:
      Rp = (((1+Rp1) x (1+Rp2) x ... x (1+Rn))^1/n) - 1
      where Rp is the total portfolio return, and there are n months in the period.

      Trailing returns are annualized for periods longer than one year using the following formula:
      Rp,annualized = ((1 + Rp)^(12/n)) - 1, where Rp is the total portfolio return, and n is the number of months in the period.

      Growth of a hypothetical portfolio is not annualized.
    </div>

    <p v-else>
      Month-end returns are presented as of {{ lastDayOfMonth }}, adjusted for splits and dividends,
      assuming monthly rebalancing to current allocation and reinvestment of dividends and capital gains.
      Returns do not account for taxes, loads, sales charges, trading commissions, short-term trading fees, management fees, or advisory fees.
      Returns coverage includes Equities, Mutual Funds, ETFs, and Cash.
      <span v-if="portfolio">
        These returns calculations cover
        {{ portfolio.trailing_returns_monthly_rebalance?.[2].toLocaleString('en-US', {style: 'percent'}) }}
        of {{ portfolioName }} overall.
      </span>
      <span v-if="benchmark">
        These returns calculations cover
        {{ benchmark.trailing_returns_monthly_rebalance?.[2].toLocaleString('en-US', {style: 'percent'}) }}
        of {{ benchmarkName }} overall.
      </span>
      <span v-if="optimizedPortfolio">
        These returns calculations cover
        {{ optimizedPortfolio.trailing_returns_monthly_rebalance?.[2].toLocaleString('en-US', {style: 'percent'}) }}
        of {{ optimizedPortfolioName }} overall.
      </span>
      See full methodology below.
    </p>
  </div>
</template>


<script>
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
export default {
  name: 'ReturnsMethodology',
  props: ['displayType', 'portfolio', 'benchmark', 'optimizedPortfolio'],
  computed: {
    ...mapStores(useCheckupStore),
    lastDayOfMonth() {
      const today = new Date();

      // 1. If there's a created date and it's in a different month/year than today, use it:
      if (this.checkupStore.portfolio_checkup_created_date) {
        const createdDate = new Date(this.checkupStore.portfolio_checkup_created_date);
        if (!isNaN(createdDate)) {
          // Only use createdDate if its month or year differs from today’s
          if (
            createdDate.getMonth() !== today.getMonth() ||
            createdDate.getFullYear() !== today.getFullYear()
          ) {
            // Move to the “zero‐th” day of the next month → last day of createdDate’s month
            const temp = new Date(createdDate);
            temp.setDate(0);

            const day = temp.getDate();
            const month = temp.getMonth() + 1;
            const year = temp.getFullYear();
            return `${month}/${day}/${year}`;
          }
        }
      }

      // 2. Otherwise, try to pull “1_yr_returns” from portfolio → benchmark → optimizedPortfolio:
      const oneYr =
        this.portfolio?.trailing_returns_monthly_rebalance?.[3]?.['1_yr_returns'] ||
        this.benchmark?.trailing_returns_monthly_rebalance?.[3]?.['1_yr_returns'] ||
        this.optimizedPortfolio?.trailing_returns_monthly_rebalance?.[3]?.['1_yr_returns'];

      if (oneYr && Object.keys(oneYr).length) {
        // Keys are in “YYYY-MM-DD” form; pick the latest and parse it:
        const dates = Object.keys(oneYr);
        const lastDateKey = dates.reduce((a, b) => (a > b ? a : b), dates[0]);
        const parsed = new Date(lastDateKey);
        const m = parsed.getMonth() + 1;
        const d = parsed.getDate();
        const y = parsed.getFullYear();
        return `${m}/${d}/${y}`;
      }

      // 3. If neither of the above yields a date, fall back to the last day of the previous month:
      today.setDate(0);
      const mm = today.getMonth() + 1;
      const dd = today.getDate();
      const yy = today.getFullYear();
      return `${mm}/${dd}/${yy}`;
    },
    portfolioName() {
      return this.portfolio?.name || 'Portfolio';
    },
    benchmarkName() {
      return this.benchmark?.name || 'Benchmark';
    },
    optimizedPortfolioName() {
      return this.optimizedPortfolio?.name || 'Optimized Portfolio';
    },
  },
};
</script>
